import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { createBranch, listBranches } from '../lib/api';

/**
 * Admin Branches & Stockists — list view. Click a row to drill into
 * `/branches/:id` for inventory, cashiers, POS, shipping, and (stockists only)
 * owner management.
 */
export function BranchesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const branchesQuery = useQuery({
    queryKey: ['admin-branches'],
    queryFn: listBranches,
  });
  const [creating, setCreating] = useState(false);

  const create = useMutation({
    mutationFn: createBranch,
    meta: { successMessage: 'Branch created.' },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['admin-branches'] });
      setCreating(false);
      navigate(`/branches/${created.id}`);
    },
  });

  const rows = branchesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Branches &amp; Stockists</h1>
        <Button onClick={() => setCreating(true)}>+ New branch</Button>
      </div>

      {creating && (
        <BranchForm
          submitting={create.isPending}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
          error={create.error}
        />
      )}

      <Card>
        <CardContent className="p-0">
          {branchesQuery.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No branches yet. Create one to start managing inventory and cashiers.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">E-com</th>
                  <th className="px-4 py-2 font-medium">POS</th>
                  <th className="px-4 py-2 font-medium">Shipping</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const isStockist = b.type === 'stockist';
                  return (
                    <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono text-xs">
                        <Link to={`/branches/${b.id}`} className="hover:underline">
                          {b.code}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <Link to={`/branches/${b.id}`} className="font-medium hover:underline">
                          {b.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={isStockist ? 'outline' : 'secondary'}
                          className="capitalize"
                        >
                          {b.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        {b.ecommerceReady ? (
                          <Badge variant="success">Ready</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Off</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {b.completionMode === 'manual' ? 'Manual release' : 'Auto'}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {b.shippingOrigin ? (
                          <span className="text-muted-foreground font-mono">
                            {b.shippingOrigin}
                          </span>
                        ) : (
                          <span className="text-warning">No origin</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BranchForm({
  submitting,
  onSubmit,
  onCancel,
  error,
}: {
  submitting: boolean;
  onSubmit: (input: { code: string; name: string; type: 'branch' | 'stockist' }) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'branch' | 'stockist'>('branch');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({ code, name, type });
  }

  const errMsg = (error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;

  return (
    <Card className="bg-muted/30">
      <CardHeader>
        <CardTitle>New branch</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="code">
              Code
            </label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="MAIN, NORTH-01, …"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="name">
              Name
            </label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <div className="flex gap-2">
              {(['branch', 'stockist'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`flex-1 py-1.5 rounded-md border text-sm capitalize ${
                    type === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-accent'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 flex items-end">
            <p className="text-xs text-muted-foreground">
              Discounts are managed in the <span className="font-medium">Discounts</span> tab.
            </p>
          </div>
          {errMsg && <p className="md:col-span-2 text-sm text-destructive">{errMsg}</p>}
          <div className="md:col-span-2 flex gap-2 pt-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Create'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
